import Intercom, { trackEvent as trackIntercomEvent, update } from '@intercom/messenger-js-sdk';
import posthog from 'posthog-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { areAnalyticsEnabledInEnv } from '../areAnalyticsEnabledInEnv';
import useQueueUntilReady from '../utils/useQueueUntilReady';
import type { Integration, UserData } from './types';

const useIntercomIntegration = (): Integration => {
  const [isInitialized, setIsInitialized] = useState(false);
  const enabled = isIntercomEnabled();
  const initializationState = useRef<'idle' | 'loading' | 'done'>('idle');
  const posthogListenerAttached = useRef(false);

  const initialize = useCallback(() => {
    if (!isIntercomEnabled() || !process.env.INTERCOM_APP_ID) return;

    if (initializationState.current !== 'idle') return;
    initializationState.current = 'loading';

    Intercom({ app_id: process.env.INTERCOM_APP_ID });

    initializationState.current = 'done';
    setIsInitialized(true);
  }, []);

  const identifyUser = useCallback(({ commonData, intercomData }: UserData) => {
    if (!isIntercomEnabled() || !intercomData) return;

    update({
      user_id: commonData.id,
      user_hash: intercomData.intercomHash,
      email: commonData.email,
      name: commonData.fullName,
      jobTitle: intercomData.jobTitle,
      type: commonData.type,
      languageOverride: commonData.language,
      company: commonData.company,
      'Recent replay': posthog.get_session_replay_url(),
    });
  }, []);
  const initAwareIdentifyUser = useQueueUntilReady(identifyUser, isInitialized);

  const resetUserIdentity = useCallback(() => {
    if (!isIntercomEnabled()) return;

    update({});
  }, []);
  const initAwareResetUserIdentity = useQueueUntilReady(resetUserIdentity, isInitialized);

  const trackEvent = useCallback((eventName: string, eventProps: Record<string, unknown>) => {
    trackIntercomEvent(eventName, eventProps);
  }, []);
  const initAwareTrackEvent = useQueueUntilReady(trackEvent, isInitialized);

  useEffect(() => {
    if (!isInitialized || posthogListenerAttached.current) {
      return;
    }

    const syncReplayUrl = () => {
      const replayUrl = posthog.get_session_replay_url();
      // Check if a URL is available (it might not be immediately after session start)
      if (replayUrl) {
        update({ 'Recent replay': replayUrl });
      }
    };

    // Attempt to sync immediately in case PostHog session already exists
    syncReplayUrl();

    // Sync whenever PostHog session ID changes
    posthog.onSessionId(syncReplayUrl);
    posthogListenerAttached.current = true;

    // PostHog JS doesn't have a documented way to remove this listener.
    // It will persist as long as the posthog object exists. The useRef prevents re-adding it.
  }, [isInitialized]);

  return useMemo(
    () => ({
      name: 'intercom',
      enabled,
      initialize,
      identifyUser: initAwareIdentifyUser,
      resetUserIdentity: initAwareResetUserIdentity,
      trackEvent: initAwareTrackEvent,
    }),
    [enabled, initAwareIdentifyUser, initialize, initAwareResetUserIdentity, initAwareTrackEvent],
  );
};

const isIntercomEnabled = () =>
  areAnalyticsEnabledInEnv() &&
  Boolean(process.env.INTERCOM_APP_ID) &&
  process.env.REACT_APP_CYPRESS !== 'true';

export default useIntercomIntegration;
